import { createSequence } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import {
  IapPollListItem,
  IapPollListItemSkeleton,
} from '~/components/molecules/IapPollListItem';
import { Pagination } from '~/components/molecules/Pagination';
import { Table } from '~/components/molecules/Table';
import { usePagination } from '~/hooks/usePagination';
import { useIapPollsQuery } from '~/lib/api/modules/IapPolls/hooks/useIapPollsQuery';

const GRID_TEMPLATE_COLUMNS = 'repeat(2, 1fr) repeat(1, min-content)';
const DEFAULT_SKELETON_COUNT = 4;

export type IIapPollListProps = IContainerProps & {
  appId: string;
};

export const IapPollList: React.FC<IIapPollListProps> = (props) => {
  const { sx, appId } = props;
  const { t } = useTranslation();
  const pagination = usePagination();
  const iapPollsQuery = useIapPollsQuery(
    {
      appId,
      body: {
        start: pagination.start,
        limit: pagination.limit,
      },
    },
    {
      enabled:
        Number.isInteger(pagination.start) &&
        Number.isInteger(pagination.limit),
      keepPreviousData: true,
    },
  );

  const iapPolls = iapPollsQuery.data;
  const isEmpty = !iapPollsQuery.isLoading && !iapPolls?.items?.length;

  return (
    <ContentBlock sx={sx}>
      {iapPollsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={iapPollsQuery.error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('IapPollList.empty.title')}>
          <Button href={`/apps/${appId}/iapPolls/create`}>
            {t('IapPollList.empty.actionLabel')}
          </Button>
        </ContentBlock.Empty>
      ) : (
        <>
          <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
            {iapPollsQuery.isLoading || iapPollsQuery.isPreviousData
              ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                  <IapPollListItemSkeleton key={index} first={index === 0} />
                ))
              : iapPolls?.items?.map((iapPoll, iapPollIndex) => (
                  <IapPollListItem
                    key={iapPoll._id}
                    iapPoll={iapPoll}
                    first={iapPollIndex === 0}
                  />
                ))}
          </Table>

          <ContentBlock.Footer>
            <Pagination
              pagination={pagination}
              total={iapPollsQuery.data?.totalCount}
            />
          </ContentBlock.Footer>
        </>
      )}
    </ContentBlock>
  );
};
